function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { nanoid } from 'nanoid';
import { assertIsDashboardPluginProps, LayoutUtils, PanelEvent, updateDashboardData, useListener } from '@deephaven/dashboard';
import Log from '@deephaven/log';
import { TextUtils } from '@deephaven/utils';
import { InputFilterEvent } from "./events/index.js";
import { DropdownFilterPanel, FilterSetManagerPanel, InputFilterPanel } from "./panels/index.js";
import { useFilterChangedListener, useFilterColumnsChangedListener, useFilterTableChangedListener } from "./FilterEvents.js";
var log = Log.module('FilterPlugin');
export function FilterPlugin(props) {
  assertIsDashboardPluginProps(props);
  var localDashboardId = props.id,
    layout = props.layout,
    registerComponent = props.registerComponent;
  var dispatch = useDispatch();
  var _useState = useState(() => new Map()),
    _useState2 = _slicedToArray(_useState, 1),
    panelColumns = _useState2[0];
  var _useState3 = useState(() => new Map()),
    _useState4 = _slicedToArray(_useState3, 1),
    panelFilters = _useState4[0];
  var _useState5 = useState(() => new Map()),
    _useState6 = _slicedToArray(_useState5, 1),
    panelTables = _useState6[0];
  var sendUpdate = useCallback(() => {
    var columns = Array.from(panelColumns.values()).flat().sort((a, b) => {
      var aName = TextUtils.toLower(a.name);
      var bName = TextUtils.toLower(b.name);
      if (aName !== bName) {
        return aName > bName ? 1 : -1;
      }
      var aType = TextUtils.toLower(a.type);
      var bType = TextUtils.toLower(b.type);
      if (aType !== bType) {
        return aType > bType ? 1 : -1;
      }
      return 0;
    }).reduce((array, column) => {
      if (array.length === 0 || TextUtils.toLower(array[array.length - 1].name) !== TextUtils.toLower(column.name) || TextUtils.toLower(array[array.length - 1].type) !== TextUtils.toLower(column.type)) {
        array.push(column);
      }
      return array;
    }, []);
    var filters = Array.from(panelFilters.values()).flat().sort((a, b) => a.timestamp - b.timestamp);
    var tableMap = new Map(panelTables);
    log.debug('sendUpdate', {
      columns,
      filters,
      tableMap
    });
    dispatch(updateDashboardData(localDashboardId, {
      columns,
      filters,
      tableMap
    }));
  }, [dispatch, localDashboardId, panelColumns, panelFilters, panelTables]);

  /**
   * Handler for the COLUMNS_CHANGED event.
   * @param sourceId The id of the component that's emitting the filter change
   * @param columns The columns in this panel. Null to clear the columns.
   */
  var handleColumnsChanged = useCallback((sourceId, columns) => {
    log.debug2('handleColumnsChanged', sourceId, columns);
    if (columns == null) {
      panelColumns.delete(sourceId);
    } else {
      panelColumns.set(sourceId, [].concat(columns));
    }
    sendUpdate();
  }, [panelColumns, sendUpdate]);

  /**
   * Handler for the FILTERS_CHANGED event.
   * @param sourceId The id of the component that's emitting the filter change
   * @param filters The input filters set by the panel
   */
  var handleFiltersChanged = useCallback((sourceId, filters) => {
    log.debug2('handleFiltersChanged', sourceId, filters);
    if (filters == null) {
      panelFilters.delete(sourceId);
    } else {
      panelFilters.set(sourceId, [].concat(filters !== null && filters !== void 0 ? filters : []));
    }
    sendUpdate();
  }, [panelFilters, sendUpdate]);
  var handleTableChanged = useCallback((sourceId, table) => {
    log.debug2('handleTableChanged', sourceId, table);
    if (table == null) {
      panelTables.delete(sourceId);
    } else {
      panelTables.set(sourceId, table);
    }
    sendUpdate();
  }, [panelTables, sendUpdate]);
  var handlePanelUnmount = useCallback(panel => {
    log.debug2('handlePanelUnmount', panel);
    var panelId = LayoutUtils.getIdFromPanel(panel);
    if (panelId != null) {
      panelColumns.delete(panelId);
      panelTables.delete(panelId);
      panelFilters.delete(panelId);
    }
    sendUpdate();
  }, [panelColumns, panelFilters, panelTables, sendUpdate]);
  var handleOpenDropdown = useCallback(_ref => {
    var _ref$title = _ref.title,
      title = _ref$title === void 0 ? 'DropdownFilter' : _ref$title,
      _ref$metadata = _ref.metadata,
      metadata = _ref$metadata === void 0 ? {} : _ref$metadata,
      _ref$panelState = _ref.panelState,
      panelState = _ref$panelState === void 0 ? null : _ref$panelState,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? nanoid() : _ref$id,
      _ref$focusElement = _ref.focusElement,
      focusElement = _ref$focusElement === void 0 ? LayoutUtils.DEFAULT_FOCUS_SELECTOR : _ref$focusElement,
      _ref$createNewStack = _ref.createNewStack,
      createNewStack = _ref$createNewStack === void 0 ? false : _ref$createNewStack,
      _ref$dragEvent = _ref.dragEvent,
      dragEvent = _ref$dragEvent === void 0 ? undefined : _ref$dragEvent;
    var config = {
      type: 'react-component',
      component: DropdownFilterPanel.COMPONENT,
      props: {
        id,
        metadata,
        panelState,
        localDashboardId
      },
      title,
      id
    };
    var root = layout.root;
    LayoutUtils.openComponent({
      root,
      config,
      focusElement,
      createNewStack,
      dragEvent
    });
  }, [layout, localDashboardId]);
  var handleOpenInput = useCallback(_ref2 => {
    var _ref2$title = _ref2.title,
      title = _ref2$title === void 0 ? 'InputFilter' : _ref2$title,
      _ref2$metadata = _ref2.metadata,
      metadata = _ref2$metadata === void 0 ? {} : _ref2$metadata,
      _ref2$panelState = _ref2.panelState,
      panelState = _ref2$panelState === void 0 ? null : _ref2$panelState,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? nanoid() : _ref2$id,
      _ref2$focusElement = _ref2.focusElement,
      focusElement = _ref2$focusElement === void 0 ? LayoutUtils.DEFAULT_FOCUS_SELECTOR : _ref2$focusElement,
      _ref2$createNewStack = _ref2.createNewStack,
      createNewStack = _ref2$createNewStack === void 0 ? false : _ref2$createNewStack,
      _ref2$dragEvent = _ref2.dragEvent,
      dragEvent = _ref2$dragEvent === void 0 ? undefined : _ref2$dragEvent;
    var config = {
      type: 'react-component',
      component: InputFilterPanel.COMPONENT,
      props: {
        id,
        metadata,
        panelState,
        localDashboardId
      },
      title,
      id
    };
    var root = layout.root;
    LayoutUtils.openComponent({
      root,
      config,
      focusElement,
      createNewStack,
      dragEvent
    });
  }, [layout, localDashboardId]);
  var handleOpenFilterSetManager = useCallback(_ref3 => {
    var _ref3$title = _ref3.title,
      title = _ref3$title === void 0 ? 'FilterSets' : _ref3$title,
      _ref3$metadata = _ref3.metadata,
      metadata = _ref3$metadata === void 0 ? {} : _ref3$metadata,
      _ref3$panelState = _ref3.panelState,
      panelState = _ref3$panelState === void 0 ? null : _ref3$panelState,
      _ref3$id = _ref3.id,
      id = _ref3$id === void 0 ? nanoid() : _ref3$id,
      _ref3$focusElement = _ref3.focusElement,
      focusElement = _ref3$focusElement === void 0 ? LayoutUtils.DEFAULT_FOCUS_SELECTOR : _ref3$focusElement,
      _ref3$createNewStack = _ref3.createNewStack,
      createNewStack = _ref3$createNewStack === void 0 ? false : _ref3$createNewStack,
      _ref3$dragEvent = _ref3.dragEvent,
      dragEvent = _ref3$dragEvent === void 0 ? undefined : _ref3$dragEvent;
    var config = {
      type: 'react-component',
      component: FilterSetManagerPanel.COMPONENT,
      props: {
        id,
        metadata,
        panelState,
        localDashboardId
      },
      title,
      id
    };
    var root = layout.root;
    LayoutUtils.openComponent({
      root,
      config,
      focusElement,
      createNewStack,
      dragEvent
    });
  }, [layout, localDashboardId]);
  useEffect(function registerComponentsAndReturnCleanup() {
    var cleanups = [registerComponent(DropdownFilterPanel.COMPONENT, DropdownFilterPanel), registerComponent(InputFilterPanel.COMPONENT, InputFilterPanel), registerComponent(FilterSetManagerPanel.COMPONENT, FilterSetManagerPanel)];
    return () => {
      cleanups.forEach(cleanup => cleanup());
    };
  }, [registerComponent]);
  useFilterColumnsChangedListener(layout.eventHub, handleColumnsChanged);
  useFilterChangedListener(layout.eventHub, handleFiltersChanged);
  useFilterTableChangedListener(layout.eventHub, handleTableChanged);
  useListener(layout.eventHub, InputFilterEvent.OPEN_DROPDOWN, handleOpenDropdown);
  useListener(layout.eventHub, InputFilterEvent.OPEN_INPUT, handleOpenInput);
  useListener(layout.eventHub, InputFilterEvent.OPEN_FILTER_SET_MANAGER, handleOpenFilterSetManager);
  useListener(layout.eventHub, PanelEvent.UNMOUNT, handlePanelUnmount);
  return null;
}
export default FilterPlugin;
//# sourceMappingURL=FilterPlugin.js.map